
import React, { useState } from 'react';
import {useTranslation} from "../../i18n";
import {formatPace} from "../../utils/formatPace";

const REASON_OPTIONS = [
  { id: 'fatigue', labelKey: 'downgrade.reason_fatigue', defaultLabel: '😴 Усталость' },
  { id: 'time', labelKey: 'downgrade.reason_time', defaultLabel: '⏱ Нехватка времени' },
  { id: 'sickness', labelKey: 'downgrade.reason_sickness', defaultLabel: '🤒 Недомогание' },
  { id: 'soreness', labelKey: 'downgrade.reason_soreness', defaultLabel: '🩹 Мышцы / Связки' },
];

export default function DowngradeModal({ workout, isOpen, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [distance, setDistance] = useState(7.0);
  const [reason, setReason] = useState('fatigue');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !workout) return null;

  // Оценочный темп (5:50 если нет точного)
  const estPace = workout.target_pace_min_per_km || 5.85;
  const estDurationMin = Math.round(distance * estPace);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(workout.id, {
        distance_km: distance,
        reason,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">

        {/* Заголовок */}
        <div className="flex items-start justify-between border-b pb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              🟢 {t('downgrade.title') || 'Понизить до лёгкого бега'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('downgrade.instead_of') || 'Вместо'}: <b className="capitalize">{t(workout.workout_type)}</b>{' '}
              ({workout.distance_km} км)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Выбор дистанции (Ползунок) */}
        <div className="space-y-3">
          <div className="flex justify-between items-baseline">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('downgrade.choose_distance') || 'Дистанция пробежки'}:
            </span>
            <span className="text-2xl font-black text-blue-600">
              {distance.toFixed(1)} <span className="text-sm font-semibold">{t('common.km')}</span>
            </span>
          </div>

          <input
            type="range"
            min="3.0"
            max="12.0"
            step="0.5"
            value={distance}
            onChange={(e) => setDistance(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />

          {/* Быстрые кнопки-чипы */}
          <div className="flex gap-2">
            {[5.0, 7.0, 9.0].map((km) => (
              <button
                key={km}
                type="button"
                onClick={() => setDistance(km)}
                className={`flex-1 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                  distance === km
                    ? 'bg-blue-50 border-blue-400 text-blue-700 shadow-xs'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {km} {t('common.km')}
              </button>
            ))}
          </div>
        </div>

        {/* Умная сводка в реальном времени */}
        <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-gray-500">{t('downgrade.estimated_time') || 'Примерное время'}:</p>
            <p className="font-bold text-gray-800 text-sm">~{estDurationMin} мин</p>
          </div>
          <div>
            <p className="text-gray-500">{t('downgrade.target_pace') || 'Целевой темп'}:</p>
            <p className="font-bold text-gray-800 text-sm">@{formatPace(estPace)} /км</p>
          </div>
        </div>

        {/* Выбор причины */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">
            {t('downgrade.reason_label') || 'Причина (для аналитики)'}:
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REASON_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setReason(opt.id)}
                className={`py-2 px-2.5 text-xs rounded-lg font-medium text-left border transition-all ${
                  reason === opt.id
                    ? 'bg-amber-50 border-amber-400 text-amber-900 shadow-xs'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t(opt.labelKey) || opt.defaultLabel}
              </button>
            ))}
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 px-4 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-200 transition-colors disabled:opacity-50"
          >
            {submitting ? '⏳...' : t('downgrade.confirm_button') || 'Подтвердить Easy'}
          </button>
        </div>

      </div>
    </div>
  );
}